import { useEffect, useReducer, useRef, useState, type Dispatch } from "react";
import { Loader2, RotateCcw, Save, Smartphone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { ComboboxMulti } from "@/components/ui/combobox_mutli";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import {
  useGetAuthPermissionRules,
  type AuthPermissionResourceRules,
  type AuthPermissionRules,
} from "@/lib/api/authUsers/rules";
import {
  AuthUserRequestError,
  useGetAuthUserPermissions,
  useUpdateAuthUser,
  type AuthPermissionScopeDimension,
  type AuthPermissionState,
  type AuthUserMembership,
  type AuthUserOrganizationType,
  type AuthUserPermissionsDetail,
  type AuthUserRelationType,
} from "@/lib/api/authUsers/users";
import type { AuthPermissionChange, UpdateAuthUserRequest } from "@/lib/validation/authUsers";
import { formatPhoneForDisplay } from "@/lib/validation/phoneNumber";
import { CustomerMultiPicker } from "@/section/permissions/components/CustomerMultiPicker";
import { changeWithState, requestFromDetail, requestToSave } from "../authUserForm";
import {
  authUserSaveErrors,
  describeAuthUserError,
  noAuthUserErrors,
  type AuthUserFormErrors,
} from "../authUserErrors";
import {
  actionLabels,
  actionsLabel,
  organizationTypeLabels,
  permissionResourceLabel,
  relationTypeLabels,
  scopeDimensionLabels,
  scopeValueLabel,
} from "../authUserLabels";

type EditorState = {
  /** The PUT body being edited. */
  request: UpdateAuthUserRequest;
  errors: AuthUserFormErrors;
};

type EditorAction =
  | { type: "reset"; request: UpdateAuthUserRequest }
  | { type: "changePermission"; change: AuthPermissionChange }
  | { type: "changeRelations"; relationType: AuthUserRelationType; customerIds: string[] }
  | { type: "saveFailed"; errors: AuthUserFormErrors };

function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case "reset":
      return { request: action.request, errors: noAuthUserErrors };
    case "changePermission":
      return {
        request: {
          ...state.request,
          permissions: state.request.permissions.map((change) =>
            change.resource === action.change.resource ? action.change : change),
        },
        errors: noAuthUserErrors,
      };
    case "changeRelations":
      return {
        request: {
          ...state.request,
          relations: [
            ...state.request.relations.filter((relation) => relation.relation_type !== action.relationType),
            ...action.customerIds.map((customerId) => ({ relation_type: action.relationType, customer_id: customerId })),
          ],
        },
        errors: noAuthUserErrors,
      };
    case "saveFailed":
      return { ...state, errors: action.errors };
  }
}

function FieldError({ message }: { message: string | undefined }) {
  return message ? <p className="text-sm font-medium text-destructive">{message}</p> : null;
}

/** The scope values a membership gets by default for a dimension. */
function defaultScopeValues(
  rules: AuthPermissionRules,
  dimension: AuthPermissionScopeDimension,
  organizationType: AuthUserOrganizationType,
): string[] {
  const scopeRules = rules.scope_dimensions[dimension];
  const inherited = scopeRules.inherited_values[organizationType];
  return inherited.mode === "all" ? scopeRules.values : inherited.values;
}

/** CustomerMultiPicker is fully controlled, so this holds its search and open state. */
function RelationPicker({
  relationType,
  maxCustomers,
  customerIds,
  onChange,
  disabled,
}: {
  relationType: AuthUserRelationType;
  maxCustomers: number | null;
  customerIds: string[];
  onChange: (customerIds: string[]) => void;
  disabled: boolean;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  return (
    <CustomerMultiPicker
      relationType={relationType}
      title={`${relationTypeLabels[relationType]} customers`}
      selectedCustomerIds={customerIds}
      onChange={onChange}
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      maxAllowed={maxCustomers ?? undefined}
      disabled={disabled}
    />
  );
}

/** full_permissible: On/Off, with the customer picker underneath while on. */
function FullPermissibleItem({
  change,
  resourceRules,
  membership,
  request,
  errors,
  dispatch,
  disabled,
}: {
  change: AuthPermissionChange;
  resourceRules: AuthPermissionResourceRules;
  membership: AuthUserMembership;
  request: UpdateAuthUserRequest;
  errors: AuthUserFormErrors;
  dispatch: Dispatch<EditorAction>;
  disabled: boolean;
}) {
  const relation = resourceRules.relation;
  const isGrantable = resourceRules.grantable_organization_types.includes(membership.organization_type);
  const testId = `auth-user-permission-${change.resource}`;

  return (
    <li className="space-y-3 px-4 py-3" data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-gray-900">{permissionResourceLabel(change.resource)}</p>
        <Switch
          checked={change.state === "on"}
          onCheckedChange={(checked: boolean) =>
            dispatch({ type: "changePermission", change: changeWithState(change, checked ? "on" : "off", resourceRules) })}
          disabled={disabled || (change.state !== "on" && !isGrantable)}
          aria-label={`${permissionResourceLabel(change.resource)} on`}
          data-testid={`${testId}-switch`}
        />
      </div>
      <FieldError message={errors.permissions[change.resource]} />
      {relation && change.state === "on" && (
        <>
          <RelationPicker
            relationType={relation.relation_type}
            maxCustomers={relation.max_customers}
            customerIds={request.relations
              .filter((candidate) => candidate.relation_type === relation.relation_type)
              .map((candidate) => candidate.customer_id)}
            onChange={(customerIds: string[]) =>
              dispatch({ type: "changeRelations", relationType: relation.relation_type, customerIds })}
            disabled={disabled}
          />
          <FieldError message={errors.relations[relation.relation_type]} />
        </>
      )}
    </li>
  );
}

/** detailed_actions: On/Off/Exclude; on shows the action checkboxes and any scopes. */
function DetailedActionsItem({
  change,
  resourceRules,
  rules,
  membership,
  errors,
  dispatch,
  disabled,
}: {
  change: AuthPermissionChange;
  resourceRules: AuthPermissionResourceRules;
  rules: AuthPermissionRules;
  membership: AuthUserMembership;
  errors: AuthUserFormErrors;
  dispatch: Dispatch<EditorAction>;
  disabled: boolean;
}) {
  const [showDefault, setShowDefault] = useState(false);
  const isGrantable = resourceRules.grantable_organization_types.includes(membership.organization_type);
  const defaultActions = resourceRules.inherited_actions[membership.effective_organization_type];
  const stateLabels: Record<AuthPermissionState, string> = {
    on: "On",
    off: `(default: ${actionsLabel(defaultActions)})`,
    exclude: "Exclude (blocked)",
  };
  const testId = `auth-user-permission-${change.resource}`;

  return (
    <li className="space-y-3 px-4 py-3" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-medium text-gray-900">{permissionResourceLabel(change.resource)}</p>
        <div className="flex flex-wrap gap-1">
          {resourceRules.states.map((state) => (
            <Button
              key={state}
              type="button"
              size="sm"
              variant={change.state !== state ? "outline" : state === "exclude" ? "destructive" : "default"}
              aria-pressed={change.state === state}
              disabled={disabled || (state !== "off" && !isGrantable)}
              onClick={() => dispatch({ type: "changePermission", change: changeWithState(change, state, resourceRules) })}
              data-testid={`${testId}-${state}`}
            >
              {stateLabels[state]}
            </Button>
          ))}
        </div>
      </div>

      {change.state === "off" && (
        <div className="space-y-2">
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0"
            onClick={() => setShowDefault(!showDefault)}
            data-testid={`${testId}-default-toggle`}
          >
            {showDefault ? "Hide what the default includes" : "See what the default includes"}
          </Button>
          {showDefault && (
            <div className="space-y-1 rounded-md border bg-gray-50/70 p-3 text-sm" data-testid={`${testId}-default`}>
              <p className="font-medium text-gray-900">
                {defaultActions.length > 0 ? defaultActions.map((action) => actionLabels[action]).join(", ") : "Nothing"}
              </p>
              {defaultActions.length > 0 && resourceRules.scope_dimensions.map((dimension) => (
                <p key={dimension} className="text-gray-600">
                  {defaultScopeValues(rules, dimension, membership.effective_organization_type).map(scopeValueLabel).join(", ")}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {"actions" in change && (
        <div className="space-y-3 rounded-md border bg-gray-50/70 p-3">
          <div className="flex flex-wrap gap-4">
            {resourceRules.allowed_actions.map((action) => (
              <label key={action} className="flex items-center gap-2 text-sm text-gray-800">
                <Checkbox
                  checked={change.actions.includes(action)}
                  onCheckedChange={(checked) => dispatch({
                    type: "changePermission",
                    change: {
                      ...change,
                      actions: resourceRules.allowed_actions.filter((allowed) =>
                        allowed === action ? checked === true : change.actions.includes(allowed)),
                    },
                  })}
                  disabled={disabled}
                  data-testid={`${testId}-action-${action}`}
                />
                {actionLabels[action]}
              </label>
            ))}
          </div>

          {resourceRules.scope_dimensions.map((dimension) => {
            const scopeRules = rules.scope_dimensions[dimension];
            const inherited = scopeRules.inherited_values[membership.effective_organization_type];
            const defaultValues = defaultScopeValues(rules, dimension, membership.effective_organization_type);
            const otherScopes = (change.scopes ?? []).filter((scope) => scope.dimension !== dimension);
            // No scope for the dimension = Default.
            const values = change.scopes?.find((scope) => scope.dimension === dimension)?.values;
            const setValues = (nextValues: string[] | undefined): void => dispatch({
              type: "changePermission",
              change: {
                ...change,
                scopes: nextValues ? [...otherScopes, { dimension, values: nextValues }] : otherScopes,
              },
            });

            return (
              <div key={dimension} className="space-y-2" data-testid={`${testId}-scope-${dimension}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {scopeDimensionLabels[dimension]}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant={values ? "outline" : "default"}
                    aria-pressed={!values}
                    disabled={disabled}
                    onClick={() => setValues(undefined)}
                  >
                    Default ({inherited.mode === "all" ? "all" : `${defaultValues.length} of ${scopeRules.values.length}`})
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={values ? "default" : "outline"}
                    aria-pressed={Boolean(values)}
                    disabled={disabled || Boolean(values)}
                    // Custom starts from the default values.
                    onClick={() => setValues([...defaultValues])}
                  >
                    Custom
                  </Button>
                </div>
                {values && (
                  <ComboboxMulti
                    dwimOnChange
                    virtualizeList
                    popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
                    id={`${testId}-scope-${dimension}-values`}
                    placeholder={`Choose ${scopeDimensionLabels[dimension].toLowerCase()}`}
                    options={scopeRules.values.map((value) => ({ value, label: scopeValueLabel(value) }))}
                    value={values}
                    onValueChange={setValues}
                    disabled={disabled}
                    maxDisplay={6}
                    className="w-full"
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
      <FieldError message={errors.permissions[change.resource]} />
    </li>
  );
}

function AuthUserEditor({
  detail,
  rules,
  onClose,
}: {
  detail: AuthUserPermissionsDetail;
  rules: AuthPermissionRules;
  /** Omitted when the editor is always shown (no Close button). */
  onClose?: () => void;
}) {
  const { toast } = useToast();
  const updateMutation = useUpdateAuthUser(detail.id);
  const [state, dispatch] = useReducer(editorReducer, undefined, (): EditorState => ({
    request: requestFromDetail(detail, detail.memberships[0]?.organization_id ?? ""),
    errors: noAuthUserErrors,
  }));

  const { request, errors } = state;
  const membership = detail.memberships.find((candidate) => candidate.organization_id === request.organization_id);
  const saved = requestFromDetail(detail, request.organization_id);
  const isDirty = JSON.stringify(request) !== JSON.stringify(saved);
  const isSaving = updateMutation.isLoading;

  const handleSave = async (): Promise<void> => {
    const withoutActions = request.permissions.find((change) => "actions" in change && change.actions.length === 0);
    if (withoutActions) {
      dispatch({
        type: "saveFailed",
        errors: { ...noAuthUserErrors, permissions: { [withoutActions.resource]: "Choose at least one action, or turn it off" } },
      });
      return;
    }

    const body = requestToSave(request, rules);
    let result: Awaited<ReturnType<typeof updateMutation.mutateAsync>>;
    try {
      result = await updateMutation.mutateAsync(body);
    } catch (error: unknown) {
      dispatch({
        type: "saveFailed",
        errors: { ...noAuthUserErrors, form: error instanceof Error ? error.message : "The changes couldn't be saved. Try again." },
      });
      return;
    }
    if (!result.success) {
      dispatch({ type: "saveFailed", errors: authUserSaveErrors(result, body) });
      return;
    }
    dispatch({ type: "reset", request: requestFromDetail(result.data, body.organization_id) });
    toast({ title: "Mobile login saved" });
  };

  return (
    <>
      <header className="flex flex-wrap items-start justify-between gap-3 border-b px-5 py-4">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-900 text-white shadow-sm">
            <Smartphone className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h3 className="flex flex-wrap items-center gap-2 text-lg font-semibold leading-tight text-gray-900">
              {detail.phone_number ? formatPhoneForDisplay(detail.phone_number) : "No phone number"}
              {detail.banned && (
                <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700 ring-1 ring-inset ring-red-200">
                  Banned
                </span>
              )}
            </h3>
            {detail.name && <p className="text-sm text-gray-600">{detail.name}</p>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {detail.memberships.length > 1 && (
            <Combobox
              dwimOnChange
              virtualizeList
              popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
              id={`auth-user-membership-${detail.id}`}
              className="w-72"
              placeholder="Choose an organization"
              options={detail.memberships.map((candidate) => ({
                value: candidate.organization_id,
                label: `${candidate.organization_name} (${organizationTypeLabels[candidate.organization_type]})`,
              }))}
              value={request.organization_id}
              // Permissions belong to the membership, so switching starts again from its saved state.
              onValueChange={(organizationId: string) => {
                if (organizationId) dispatch({ type: "reset", request: requestFromDetail(detail, organizationId) });
              }}
              disabled={isSaving}
            />
          )}
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose} disabled={isSaving} data-testid="auth-user-panel-close">
              <X className="mr-1 h-4 w-4" />
              Close
            </Button>
          )}
        </div>
      </header>

  <div className="flex flex-col space-y-4 m-4">
{membership ? (
            <>
              <ul className="divide-y rounded-lg border bg-white">
                <li className="flex items-center justify-between gap-3 px-4 py-3" data-testid="auth-user-permission-mobile">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Mobile (login)</p>
                    <p className="text-xs text-gray-500">Can login to the mobile app.</p>
                  </div>
                  <Switch checked disabled aria-label="Mobile login always on" />
                </li>
                {request.permissions.map((change) => {
                  const resourceRules = rules.resources[change.resource];
                  const itemProps = { change, resourceRules, membership, errors, dispatch, disabled: isSaving };
                  return resourceRules.variant === "full_permissible"
                    ? <FullPermissibleItem key={change.resource} {...itemProps} request={request} />
                    : <DetailedActionsItem key={change.resource} {...itemProps} rules={rules} />;
                })}
              </ul>

              {errors.form && (
                <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="auth-user-save-error">
                  {errors.form}
                </p>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => dispatch({ type: "reset", request: saved })}
                  disabled={!isDirty || isSaving}
                  data-testid="auth-user-reset"
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Reset
                </Button>
                <Button
                  type="button"
                  onClick={() => { void handleSave(); }}
                  disabled={!isDirty || isSaving}
                  data-testid="auth-user-save"
                >
                  {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  {isSaving ? "Saving…" : "Save"}
                </Button>
              </div>
            </>
          ) : (
            <p className="rounded-lg border border-dashed bg-white px-4 py-8 text-center text-sm text-gray-500" data-testid="auth-user-no-membership">
              This login isn't in any client or non-client organization, so it has no permissions to edit here.
            </p>
          )}
  </div>
    </>
  );
}

/** Edits a client or non-client mobile login's permissions and customer links, shown under the record card that lists it. */
export function AuthUserPanel({ authUserId, onClose }: { authUserId: string; onClose?: () => void }) {
  const panelRef = useRef<HTMLElement>(null);
  const rulesResult = useGetAuthPermissionRules();
  const detailResult = useGetAuthUserPermissions(authUserId);

  useEffect((): void => {
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [authUserId]);

  const detail = detailResult.data;
  const rules = rulesResult.data;
  const loadError = detailResult.error ?? rulesResult.error;

  return (
    <section
      ref={panelRef}
      className="scroll-mt-6 overflow-hidden rounded-xl border border-blue-200 bg-white shadow-sm"
      data-testid={`auth-user-panel-${authUserId}`}
    >
      {detail && rules ? (
        // Keyed so switching to another login starts from its own saved state.
        <AuthUserEditor key={detail.id} detail={detail} rules={rules} onClose={onClose} />
      ) : loadError ? (
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
          <p className="text-sm text-red-700" data-testid="auth-user-load-error">
            {loadError instanceof AuthUserRequestError
              ? describeAuthUserError(loadError, "The mobile login couldn't be loaded.")
              : loadError instanceof Error ? loadError.message : "The mobile login couldn't be loaded."}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (detailResult.isError) void detailResult.refetch();
                if (rulesResult.isError) void rulesResult.refetch();
              }}
              disabled={detailResult.isFetching || rulesResult.isFetching}
            >
              Try again
            </Button>
            {onClose && (
              <Button variant="ghost" size="sm" onClick={onClose} data-testid="auth-user-panel-close">
                <X className="mr-1 h-4 w-4" />
                Close
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3 px-5 py-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-40 rounded-lg" />
        </div>
      )}
    </section>
  );
}
